import { Injectable } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { AuctionStatusEnum } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.models'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src'
import { ItemFacade, PopperPayload } from 'libs/gb-front/item/data-access/src'
import { BehaviorSubject, combineLatest, map, Observable, take } from 'rxjs'
import { GbPopperComponent } from './gb-popper.component'

@Injectable({
  providedIn: 'root',
})
export class GbPopperService {
  constructor(
    private matSnackBar: MatSnackBar,
    private itemFacade: ItemFacade,
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
  ) {}

  /**
   * Enable poppers subject$ of gb popper service
   * Use This subject to hide / show the poppers temporarily, for example, while the user
   * is viewing the register form.
   */

  enablePoppersSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)

  enablePoppers$ = this.enablePoppersSubject$.asObservable()

  popperDuration = 5000
  showingPopper = false
  panelClass = 'gb-popper'
  poppers: PopperPayload[] = []

  shouldShowPoppers$: Observable<boolean> = combineLatest([
    this.itemFacade.showPoppers$,
    this.auctionFacade.auctionStatus$,
    this.enablePoppers$,
  ]).pipe(
    map(([showPopper, status, enablePoppers]) => {
      return !!(showPopper && status === AuctionStatusEnum.Running && enablePoppers)
    }),
  )

  enablePoppers(): void {
    this.enablePoppersSubject$.next(true)
  }

  disablePoppers(): void {
    this.enablePoppersSubject$.next(false)
  }

  createPopper(payload: PopperPayload): void {
    this.authFacade.authProfileId$.pipe(take(1)).subscribe((selectedProfileId) => {
      if (selectedProfileId !== payload.profile) {
        this.addPopper(payload)
      }
    })

    if (!this.showingPopper) {
      this.showPoppers()
    }
  }

  addPopper(payload: PopperPayload): void {
    if (this.poppers.length >= 5) {
      this.poppers.splice(0, 1)
    }

    this.poppers.push(payload)
  }

  showPoppers(): void {
    this.showingPopper = true

    const popper = this.poppers.shift()
    if (popper) {
      this.showPopper(popper)
    }

    setTimeout(() => {
      this.poppers.length > 0 ? this.showPoppers() : (this.showingPopper = false)
    }, this.popperDuration)
  }

  showPopper(popper: PopperPayload): void {
    this.shouldShowPoppers$.pipe(take(1)).subscribe((shouldShow) => {
      if (shouldShow) {
        this.showSnackBar(popper)
      }
    })
  }

  showSnackBar(popper: PopperPayload): void {
    this.matSnackBar.openFromComponent(GbPopperComponent, {
      duration: this.popperDuration,
      data: popper,
      panelClass: this.panelClass,
    })
    this.updatePopperZIndex()
  }

  /**
   * The below ensures the popper is shown behind any dialogs
   */
  updatePopperZIndex(): void {
    const popperOverlay = document
      .querySelector(`.${this.panelClass}`)
      ?.closest('.cdk-global-overlay-wrapper') as HTMLElement
    if (popperOverlay) popperOverlay.style.zIndex = '999'
  }
}
